import { useEffect, useRef, useState } from "react";
import { styled } from "~/application/theme";
import { Spinner } from "~/components/Spinner";
import { Text } from "~/components/Text";
import { Flex } from "~/components/Flex";
import useMobile from "~/presentation/shared/hooks/useMobile";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { sidebarService } from "~/services/SidebarService";
import { bottomSheetService } from "~/components/BottomSheet/BottomSheetService";
import { orderCartService } from "~/services/OrderCartService";

const MAX_PULL_DISTANCE_PX = 140;
const TRIGGER_THRESHOLD_PX = 80;

const IndicatorWrapper = styled("div", {
  position: "fixed",
  left: 0,
  right: 0,
  top: 0,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 9999,
});

const Indicator = styled("div", {
  display: "inline-flex",
  alignItems: "center",
  gap: "$2",
  padding: "$2 $3",
  backgroundColor: "rgba(255,255,255,0.95)",
  borderRadius: "$md",
  boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
  transform: "translateY(-100%)",
  opacity: 0,
  transition: "transform $transitions$fast, opacity $transitions$fast",
});

export function PullToRefresh() {
  const isMobile = useMobile();
  const location = useLocation();
  const queryClient = useQueryClient();

  const startYRef = useRef<number | null>(null);
  const startXRef = useRef<number | null>(null);
  const pullingRef = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasOverlaysOpen, setHasOverlaysOpen] = useState(false);

  const isPublicRoute = location.pathname === "/entrar" || location.pathname === "/redefinir-senha";

  // Função para verificar se há overlays abertos
  const checkOverlaysState = () => {
    const hasSidebar = sidebarService.hasSidebar();
    const hasBottomSheet = bottomSheetService.hasBottomSheet();
    const hasOrderCart = orderCartService.hasOpenCart();
    const hasOverlays = hasSidebar || hasBottomSheet || hasOrderCart;
    
    setHasOverlaysOpen(hasOverlays);
  };

  // Monitora mudanças nos services
  useEffect(() => {
    checkOverlaysState();
    
    const sidebarUnsubscribe = sidebarService.subscribe(() => {
      checkOverlaysState();
    });
    
    const bottomSheetUnsubscribe = bottomSheetService.subscribe(() => {
      checkOverlaysState();
    });

    const orderCartUnsubscribe = orderCartService.subscribe(() => {
      checkOverlaysState();
    });

    return () => {
      sidebarUnsubscribe();
      bottomSheetUnsubscribe();
      orderCartUnsubscribe();
    };
  }, []);
  const isEnabled = isMobile && !isPublicRoute && !hasOverlaysOpen;

  useEffect(() => {
    if (!isEnabled) return;

    const scrollElement = document.scrollingElement || document.documentElement;

    const onTouchStart = (e: TouchEvent) => {
      if (isRefreshing) return;
      // Evita conflitos com inputs/textarea
      const target = e.target as HTMLElement;
      if (target && (target.closest("input, textarea, select, [contenteditable=true]") || target.isContentEditable)) return;

      // Evita conflitos com o cart de airway (drag to close)
      if (target && target.closest('[data-airway-cart="true"]')) return;

      if ((scrollElement?.scrollTop ?? 0) <= 0) {
        startYRef.current = e.touches[0].clientY;
        startXRef.current = e.touches[0].clientX;
        pullingRef.current = true;
        setPullDistance(0);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pullingRef.current || startYRef.current === null || startXRef.current === null) return;
      
      // Verifica se o touch está dentro do cart de airway
      const target = e.target as HTMLElement;
      if (target && target.closest('[data-airway-cart="true"]')) {
        pullingRef.current = false;
        startYRef.current = null;
        startXRef.current = null;
        setPullDistance(0);
        return;
      }
      
      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const deltaY = currentY - startYRef.current;
      const deltaX = currentX - startXRef.current;

      // Se o movimento horizontal for maior que o vertical, cancela o pull-to-refresh
      // Isso permite que elementos com scroll horizontal (como abas) funcionem corretamente
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        pullingRef.current = false;
        startYRef.current = null;
        startXRef.current = null;
        setPullDistance(0);
        return;
      }

      if (deltaY > 0) {
        // Previne o rubber band padrão enquanto puxamos
        if (e.cancelable) e.preventDefault();
        const eased = Math.min(MAX_PULL_DISTANCE_PX, deltaY * 0.6);
        setPullDistance(eased);
      } else {
        pullingRef.current = false;
        startYRef.current = null;
        startXRef.current = null;
        setPullDistance(0);
      }
    };

    const onTouchEnd = async () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      startYRef.current = null;
      startXRef.current = null;

      if (pullDistance >= TRIGGER_THRESHOLD_PX && !isRefreshing) {
        setIsRefreshing(true);
        try {
          await queryClient.refetchQueries({ type: "active" });
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
        }
      } else {
        setPullDistance(0);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onTouchStart as EventListener);
      window.removeEventListener("touchmove", onTouchMove as EventListener);
      window.removeEventListener("touchend", onTouchEnd as EventListener);
    };
  }, [isEnabled, isRefreshing, pullDistance, queryClient, hasOverlaysOpen]);

  const visible = isEnabled && (isRefreshing || pullDistance > 0);
  const reachedThreshold = pullDistance >= TRIGGER_THRESHOLD_PX;

  return (
    <IndicatorWrapper aria-hidden={!visible} style={{ pointerEvents: "none" }}>
      <Indicator
        style={{
          transform: visible ? `translateY(${isRefreshing ? 56 : Math.max(0, pullDistance - 24)}px)` : "translateY(-100%)",
          opacity: visible ? 1 : 0,
        }}
      >
        {isRefreshing ? (
          <Spinner size="sm" />
        ) : (
          <Spinner size="sm" />
        )}
        <Text size={2} variant="dark">
          {isRefreshing ? "Atualizando..." : reachedThreshold ? "Solte para atualizar" : "Puxe para atualizar"}
        </Text>
      </Indicator>
    </IndicatorWrapper>
  );
}

PullToRefresh.displayName = "PullToRefresh";


